import React, { useState, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

export default function ChatLoginPanel({ ssoEnabled = false }) {
  const { login } = useContext(AuthContext);

  // -----------------------------
  // STATE
  // -----------------------------
  const [messages, setMessages] = useState([
    { from: "kwery", text: "Hey there 👋" },
    { from: "kwery", text: "Let me get things ready for you…" }
  ]);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatStep, setChatStep] = useState("init");  
  const [username, setUsername] = useState("");

  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const pushKwery = (text) => {
    setMessages((m) => [...m, { from: "kwery", text }]);
  };

  const pushUser = (text) => {
    setMessages((m) => [...m, { from: "user", text }]);
  };

  // =====================================================================
  // 🔥 AUTO SSO FLOW -----------------------------------------------------
  // =====================================================================
  useEffect(() => {
    if (!ssoEnabled) return;

    const startAutoSSO = async () => {
      pushKwery("I see you're using single sign-on… Let me check that for you.");

      try {
        const res = await fetch(`${API_BASE}/auth/sso-auto-login`, {
          method: "POST",
          credentials: "include"
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "SSO failed");

        await login(data);

        pushKwery(`Hey ${data.user.name}, great to see you again 😊`);
        pushKwery("You're all set — taking you in…");

        setTimeout(() => {
          window.location.href = "/staff-dashboard";
        }, 1200);

      } catch (err) {
        pushKwery("Looks like SSO isn't available right now.");
        pushKwery("Shall we sign in the usual way?");
        setChatStep("ask-returning");
      }
    };

    startAutoSSO();
  }, [ssoEnabled, login]);

  // =====================================================================
  // 🔥 MAIN CHAT LOGIN FLOW
  // =====================================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userText = input.trim();
    pushUser(userText);
    setInput("");

    // ------------------------------
    // ASK: ARE YOU A RETURNING USER?
    // ------------------------------
    if (chatStep === "ask-returning") {
      if (userText.toLowerCase() === "yes") {
        pushKwery("Great! What's your username?");
        setChatStep("get-username");
      } else {
        pushKwery("No worries — let's get to know you a bit.");
        pushKwery("What's your name?");
        setChatStep("new-user-name");
      }
      return;
    }

    // ------------------------------
    // USERNAME
    // ------------------------------
    if (chatStep === "get-username") {
      setUsername(userText);
      pushKwery("And your password?");
      setChatStep("get-password");
      return;
    }

    // ------------------------------
    // PASSWORD → AUTHENTICATE
    // ------------------------------
    if (chatStep === "get-password") {
      setLoading(true);
      pushKwery("Alright, one moment…");

      try {
        const res = await fetch(`${API_BASE}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: username,
            password: userText,
          }),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Login failed");

        await login(data);

        pushKwery(`Hey ${data.user.name}, great to see you again 😊`);
        pushKwery("You're all set — taking you in…");

        setTimeout(() => {
          window.location.href = "/staff-dashboard";
        }, 1200);

      } catch (err) {
        pushKwery("Hmm… that didn't go through. Want to try again?");
        setChatStep("ask-returning");
      }

      setLoading(false);
      return;
    }

    // =====================================================================
    // 🔥 NEW USER / LEAD CAPTURE FLOW -------------------------------------
    // =====================================================================

    if (chatStep === "new-user-name") {
      pushKwery(`Nice to meet you, ${userText}! What's your role in your company?`);
      setChatStep("new-user-role");
      return;
    }

    if (chatStep === "new-user-role") {
      pushKwery("Got it. What's your company name?");
      setChatStep("new-user-company");
      return;
    }

    if (chatStep === "new-user-company") {
      pushKwery("How many employees do you have?");
      setChatStep("new-user-employees");
      return;
    }

    if (chatStep === "new-user-employees") {
      pushKwery("And what are you mainly looking for?");
      setChatStep("new-user-needs");
      return;
    }

    if (chatStep === "new-user-needs") {
      pushKwery("Amazing — would you like us to get in touch with a personalized setup?");
      setChatStep("new-user-confirm");
      return;
    }

    if (chatStep === "new-user-confirm") {
      if (userText.toLowerCase() === "yes") {
        pushKwery("Awesome! What's your email?");
        setChatStep("new-user-email");
      } else {
        pushKwery("All good 😊 If you ever need us, we'll be here.");
      }
      return;
    }

    if (chatStep === "new-user-email") {
      pushKwery("And your phone number?");
      setChatStep("new-user-phone");
      return;
    }

    if (chatStep === "new-user-phone") {
      pushKwery("Perfect — we'll reach out shortly 🚀");
      setChatStep("completed");
      return;
    }
  };

  // =====================================================================
  // 🔥 UI RENDER
  // =====================================================================

  return (
    <div className="h-full w-full bg-[#0B0B0B] flex flex-col justify-between p-6">

      {/* CHAT HISTORY */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`p-3 rounded-xl max-w-[80%] text-sm ${
              msg.from === "kwery"
                ? "bg-white/10 text-[#00FF99] self-start"
                : "bg-[#00FF99] text-black self-end"
            }`}
          >
            {msg.text}
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* INPUT BAR */}
      {chatStep !== "completed" && (
        <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
          <input
            className="flex-1 bg-black/40 border border-[#00FF99]/30 rounded-lg px-3 py-2 text-white text-sm"
            placeholder="Type your reply…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
          />
          <button
            className="px-4 bg-[#00FF99] text-black rounded-lg font-semibold"
            disabled={loading}
          >
            Send
          </button>
        </form>
      )}
    </div>
  );
}
